/**
 * useCaseActivityRefresh — refetch-on-case-activity primitive (RA-LIVE-HOOK).
 *
 * Calls `onChange` (trailing-debounced) when a `case.activity_recorded` / `case.thread_read`
 * domain event arrives (filtered to `caseId` when given), when the realtime subscription
 * re-establishes after a drop (RA-LIVE-RECONNECT — events missed while offline), and when the
 * tab becomes visible again. The debounce / reconnect / visibility trigger is the shared
 * `createLiveRefetch` (@resida/shared), also used by the tenant case page. The latest
 * `onChange` is read through a ref, so callers need not memoize it.
 */

import { useCallback, useEffect, useRef } from 'react';
import { bindLiveRefetchToDocument, createLiveRefetch, type LiveRefetch } from '@resida/shared';
import { useDomainEvents } from './useDomainEvents';
import { useCentrifugoContext } from '../contexts/CentrifugoContext';

export function useCaseActivityRefresh(opts: {
  caseId?: string;
  onChange: () => void;
  debounceMs?: number;
}): void {
  const { caseId, onChange, debounceMs } = opts;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const liveRefetchRef = useRef<LiveRefetch | null>(null);

  useEffect(() => {
    const liveRefetch = createLiveRefetch({ onChange: () => onChangeRef.current(), debounceMs });
    liveRefetchRef.current = liveRefetch;
    const unbind = bindLiveRefetchToDocument(liveRefetch, document);
    return () => {
      unbind();
      liveRefetchRef.current = null;
    };
  }, [debounceMs]);

  const handleEvent = useCallback(
    (event: { caseId: string }) => {
      if (caseId && event.caseId !== caseId) return;
      liveRefetchRef.current?.trigger();
    },
    [caseId],
  );

  useDomainEvents({
    'case.activity_recorded': handleEvent,
    'case.thread_read': handleEvent,
  });

  const { isSubscribed } = useCentrifugoContext();
  useEffect(() => {
    liveRefetchRef.current?.onSubscribedChange(isSubscribed);
  }, [isSubscribed]);
}
